import React, { useState } from "react";
import { FilePond, registerPlugin } from "react-filepond";
import { toast } from "react-toastify";
import "filepond/dist/filepond.min.css";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";
import FilePondPluginFileValidateSize from "filepond-plugin-file-validate-size";
import { HiddenColumnsNote, hiddenColumns, visibleColumns } from "./common";
import "./Import.css";

registerPlugin(FilePondPluginFileValidateType, FilePondPluginFileValidateSize);

// Read a cell out of a sample row that may be an object or a positional array.
function cellValue(row, columns, col) {
	if (Array.isArray(row)) {
		const idx = columns.indexOf(col);
		return idx >= 0 ? row[idx] : "";
	}
	if (row && typeof row === "object") return row[col];
	return "";
}

export default function UploadStep({ api, upload, fileName, onUploaded, onNext }) {
	const [files, setFiles] = useState([]);

	const handleProcess = async (
		fieldName,
		file,
		metadata,
		load,
		error,
		progress,
		abort
	) => {
		if (!file.name.toLowerCase().endsWith(".csv")) {
			toast.error("❌ Only .csv files are allowed.");
			error("Only CSV allowed");
			return abort();
		}

		try {
			const result = await api.uploadCsv(file);
			if (!result || !result.upload_id) {
				throw new Error("upload response is missing upload_id");
			}
			onUploaded(result, file.name);
			toast.success(
				`✅ Parsed ${result.columns?.length ?? 0} columns from ${file.name}.`
			);
			load(file.name);
		} catch (err) {
			console.error("Import upload failed:", err);
			if (err?.status !== 401 && err?.status !== 403) {
				toast.error(`❌ Upload failed: ${err.message}`, { autoClose: 8000 });
			}
			error("Upload failed");
		}
	};

	// Columns that can never be imported (scrub metadata / a copy of EXISTING
	// Iris data, or empty in every row) don't belong in the wizard at all —
	// they're summarized in the collapsed note below instead.
	const hidden = hiddenColumns(upload);
	const columns = visibleColumns(upload);
	const sampleRows = (upload?.sample_rows || []).slice(0, 5);
	const suggestedCount = upload?.suggested_mapping
		? Object.keys(upload.suggested_mapping).length
		: 0;

	return (
		<div>
			<h2>📂 Upload Lead CSV</h2>
			<p style={{ marginBottom: 10 }}>
				Drag a CSV file here or click to browse. Ideally the{" "}
				<code>green_new_leads</code> output of a scrub run — any CSV is
				accepted.
			</p>

			<div className={files.length > 0 ? "uploading" : ""}>
				<FilePond
					files={files}
					onupdatefiles={setFiles}
					className="large-uploader"
					allowMultiple={false}
					acceptedFileTypes={[
						"text/csv",
						".csv",
						"application/vnd.ms-excel",
						"application/csv",
						"text/plain",
					]}
					fileValidateTypeLabelExpectedTypesMap={{
						"text/csv": ".csv",
						"application/vnd.ms-excel": ".csv",
						"application/csv": ".csv",
						"text/plain": ".csv",
					}}
					allowFileTypeValidation={true}
					allowFileSizeValidation={true}
					maxFileSize="25MB"
					labelIdle='📂 Drag & Drop your CSV or <span class="filepond--label-action">Browse</span>'
					labelFileProcessing="Uploading…"
					labelFileProcessingComplete="✅ File parsed"
					labelFileProcessingError="❌ Upload failed"
					server={{ process: handleProcess }}
					instantUpload={true}
				/>
			</div>

			{upload && (
				<div style={{ marginTop: 18 }}>
					<h3>
						Detected columns in <code>{fileName}</code> ({columns.length})
					</h3>
					<div className="chipRow">
						{columns.map((c) => (
							<span key={c} className="columnChip">
								{c}
							</span>
						))}
					</div>
					{suggestedCount > 0 && (
						<p className="importMuted" style={{ fontSize: 13 }}>
							{suggestedCount} column{suggestedCount === 1 ? "" : "s"} auto-suggested a
							mapping — you can change everything in the next step.
						</p>
					)}
					<HiddenColumnsNote hidden={hidden} />

					{sampleRows.length > 0 && (
						<>
							<h3>Sample rows</h3>
							<div className="importTableWrap">
								<table className="importTable">
									<thead>
										<tr>
											{columns.map((c) => (
												<th key={c}>{c}</th>
											))}
										</tr>
									</thead>
									<tbody>
										{sampleRows.map((row, i) => (
											<tr key={i}>
												{columns.map((c) => (
													<td key={c}>
														{String(cellValue(row, columns, c) ?? "")}
													</td>
												))}
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</>
					)}
				</div>
			)}

			<div className="wizardNav">
				<span />
				<button
					type="button"
					className="importBtn"
					disabled={!upload}
					onClick={onNext}>
					Continue to Mapping →
				</button>
			</div>
		</div>
	);
}
